import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FiEdit2, FiTrash2, FiRefreshCw, FiSearch, FiX, FiCheckCircle, FiFileText, FiDownload } from "react-icons/fi";
import { MdOutlineArchive } from "react-icons/md";
import { approvalService, publishedService, revisionService } from "../../services/firestoreService";
import { createPDF } from "../../utils/pdfExport";
import * as XLSX from 'xlsx';
import { serverTimestamp, collection, query, limit, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import Portal from "../../components/Portal";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/contentlibrary.css";
import "../AdminPage.css";

const CONTENT_CATEGORIES = [
  "Contraception",
  "Fertility Basics",
  "STI Awareness",
  "Mental Health in Reproductive Care",
  "Male Reproductive Health",
  "Pregnancy Prevention",
  "General Reproductive Wellness",
  "Community Outreach Programs"
];

const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";
  
  // Handle Firestore timestamp objects
  let date;
  if (dateValue.toDate && typeof dateValue.toDate === 'function') {
    // Firestore timestamp object
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    // Already a Date object
    date = dateValue;
  } else {
    // Try to parse as string
    date = new Date(dateValue);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }
  
  const dateStr = date.toLocaleDateString();
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  return (
    <>
      {dateStr}<br />{timeStr}
    </>
  );
};

const formatDateModal = (dateValue) => {
  if (!dateValue) return "N/A";
  
  let date;
  if (dateValue.toDate && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }
  
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }
  
  const dateStr = date.toLocaleDateString();
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  return `${dateStr} ${timeStr}`;
};

// ---------------- Archive Modal ----------------
function ArchiveModal({ isOpen, onClose, onConfirm, approval }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      setIsLoading(false);
      onClose();
    } catch (error) {
      console.error('Error archiving content:', error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Archive Content</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#fee2e2", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <MdOutlineArchive size={32} color="#dc2626" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to archive this content?</p>
          
          {approval && (
            <div className="content-preview" style={{ 
              background: "#f9fafb", 
              padding: "12px", 
              borderRadius: "8px", 
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>{approval.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Creator:</strong> {approval.author}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Category:</strong> {approval.category}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Submitted Date:</strong> {formatDateModal(approval.submittedAt)}
                </div>
              </div>
            </div>
          )}
          
          <div style={{ 
            marginTop: "16px", 
            padding: "12px", 
            background: "#fef3c7", 
            borderRadius: "8px", 
            border: "1px solid #f59e0b"
          }}>
            <div style={{ fontSize: "14px", color: "#92400e", fontWeight: "600", textAlign: "left" }}>
              This will:
            </div>
            <ul style={{ 
              margin: "8px 0 0 0", 
              paddingLeft: "20px", 
              fontSize: "14px", 
              color: "#92400e",
              textAlign: "left"
            }}>
              <li>Remove content from resources</li>
              <li>Content will no longer be visible to users</li>
              <li>Status will change to "Archived"</li>
            </ul>
          </div>
        </div>
        
        <div className="modal-actions" style={{ display: "flex", gap: "12px" }}>
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isLoading}
            style={{ flex: 1, minWidth: "120px" }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm archive-btn ${isLoading ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              backgroundColor: "rgba(220, 38, 38, 0.15)",
              borderColor: "rgba(220, 38, 38, 0.3)",
              color: "#dc2626",
              minHeight: "40px",
              height: "40px",
              maxHeight: "40px",
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              overflow: "hidden",
              flex: 1,
              minWidth: "120px",
              boxSizing: "border-box",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#dc2626";
              e.target.style.borderColor = "#dc2626";
              e.target.style.color = "white";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(220, 38, 38, 0.15)";
              e.target.style.borderColor = "rgba(220, 38, 38, 0.3)";
              e.target.style.color = "#dc2626";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "#dc2626";
            }}
          >
            {isLoading ? 'Archiving...' : (
              <>
                <MdOutlineArchive style={{ marginRight: '4px' }} />
                Archive
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Restore Modal ----------------
function RestoreModal({ isOpen, onClose, onConfirm, approval }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      setIsLoading(false);
      onClose();
    } catch (error) {
      console.error('Error restoring content:', error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Restore Content</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#d1fae5", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <FiRefreshCw size={32} color="#059669" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to restore this content?</p>
          
          {approval && (
            <div className="content-preview" style={{ 
              background: "#f9fafb", 
              padding: "12px", 
              borderRadius: "8px", 
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>{approval.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Creator:</strong> {approval.author}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Category:</strong> {approval.category}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Submitted Date:</strong> {formatDateModal(approval.submittedAt)}
                </div>
              </div>
            </div>
          )}
          
          <div style={{ 
            marginTop: "16px", 
            padding: "12px", 
            background: "#d1fae5", 
            borderRadius: "8px", 
            border: "1px solid #10b981"
          }}>
            <div style={{ fontSize: "14px", color: "#065f46", fontWeight: "600", textAlign: "left" }}>
              This will:
            </div>
            <ul style={{ 
              margin: "8px 0 0 0", 
              paddingLeft: "20px", 
              fontSize: "14px", 
              color: "#065f46",
              textAlign: "left"
            }}>
              <li>Add content back to resources</li>
              <li>Content will be visible to users again</li>
              <li>Status will change to "Active"</li>
            </ul>
          </div>
        </div>
        
        <div className="modal-actions" style={{ display: "flex", gap: "12px" }}>
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isLoading}
            style={{ flex: 1, minWidth: "120px" }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm restore-btn ${isLoading ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              backgroundColor: "rgba(16, 185, 129, 0.15)",
              borderColor: "rgba(16, 185, 129, 0.3)",
              color: "#10b981",
              minHeight: "40px",
              height: "40px",
              maxHeight: "40px",
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              overflow: "hidden",
              flex: 1,
              minWidth: "120px",
              boxSizing: "border-box",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#10b981";
              e.target.style.borderColor = "#10b981";
              e.target.style.color = "white";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(16, 185, 129, 0.15)";
              e.target.style.borderColor = "rgba(16, 185, 129, 0.3)";
              e.target.style.color = "#10b981";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "#10b981";
            }}
          >
            {isLoading ? 'Restoring...' : (
              <>
                <FiRefreshCw style={{ marginRight: '4px' }} />
                Restore
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Delete Modal ----------------
function DeleteModal({ isOpen, onClose, onConfirm, approval }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      setIsLoading(false);
      onClose();
    } catch (error) {
      console.error('Error deleting content:', error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Delete Content</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#fee2e2", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <FiTrash2 size={32} color="#dc2626" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to delete this content? This action cannot be undone.</p>
          
          {approval && (
            <div className="content-preview" style={{
              background: "#f9fafb",
              padding: "16px",
              borderRadius: "8px",
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>{approval.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Creator:</strong> {approval.author}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Category:</strong> {approval.category}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Submitted Date:</strong> {formatDateModal(approval.submittedAt)}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-actions" style={{ display: "flex", gap: "12px" }}>
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isLoading}
            style={{ flex: 1, minWidth: "120px" }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm delete-btn ${isLoading ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              backgroundColor: "rgba(220, 38, 38, 0.15)",
              borderColor: "rgba(220, 38, 38, 0.3)",
              color: "#dc2626",
              minHeight: "40px",
              height: "40px",
              maxHeight: "40px",
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              overflow: "hidden",
              flex: 1,
              minWidth: "120px",
              boxSizing: "border-box",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#dc2626";
              e.target.style.borderColor = "#dc2626";
              e.target.style.color = "white";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(220, 38, 38, 0.15)";
              e.target.style.borderColor = "rgba(220, 38, 38, 0.3)";
              e.target.style.color = "#dc2626";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "#dc2626";
            }}
          >
            {isLoading ? 'Deleting...' : (
              <>
                <FiTrash2 style={{ marginRight: '4px' }} />
                Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Apply Revision Modal ----------------
function ApplyRevisionModal({ isOpen, onClose, onConfirm, revision }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      setIsLoading(false);
      onClose();
    } catch (error) {
      console.error('Error applying revision:', error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Apply Revision</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#d1fae5", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <FiCheckCircle size={32} color="#059669" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to apply this revision? This will update the content with the revised version.</p>
          
          {revision && (
            <div className="content-preview" style={{
              background: "#f9fafb",
              padding: "16px",
              borderRadius: "8px",
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>{revision.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Creator:</strong> {revision.author}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Category:</strong> {revision.category}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Revision Date:</strong> {formatDateModal(revision.createdAt)}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-actions" style={{ display: "flex", gap: "12px" }}>
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isLoading}
            style={{ flex: 1, minWidth: "120px" }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm ${isLoading ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              backgroundColor: "rgba(16, 185, 129, 0.15)",
              borderColor: "rgba(16, 185, 129, 0.3)",
              color: "#10b981",
              minHeight: "40px",
              height: "40px",
              maxHeight: "40px",
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              overflow: "hidden",
              flex: 1,
              minWidth: "120px",
              boxSizing: "border-box",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#10b981";
              e.target.style.borderColor = "#10b981";
              e.target.style.color = "white";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(16, 185, 129, 0.15)";
              e.target.style.borderColor = "rgba(16, 185, 129, 0.3)";
              e.target.style.color = "#10b981";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "#10b981";
            }}
          >
            {isLoading ? 'Applying...' : (
              <>
                <FiCheckCircle style={{ marginRight: '4px' }} />
                Apply Revision
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Reject Revision Modal ----------------
function RejectRevisionModal({ isOpen, onClose, onConfirm, revision }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      setIsLoading(false);
      onClose();
    } catch (error) {
      console.error('Error rejecting revision:', error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Reject Revision</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#fee2e2", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <FiX size={32} color="#dc2626" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to reject this revision? This will delete the revision without updating the content.</p>
          
          {revision && (
            <div className="content-preview" style={{
              background: "#f9fafb",
              padding: "16px",
              borderRadius: "8px",
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>{revision.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Creator:</strong> {revision.author}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Category:</strong> {revision.category}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Revision Date:</strong> {formatDateModal(revision.createdAt)}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-actions" style={{ display: "flex", gap: "12px" }}>
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isLoading}
            style={{ flex: 1, minWidth: "120px" }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm ${isLoading ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              backgroundColor: "rgba(220, 38, 38, 0.15)",
              borderColor: "rgba(220, 38, 38, 0.3)",
              color: "#dc2626",
              minHeight: "40px",
              height: "40px",
              maxHeight: "40px",
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              overflow: "hidden",
              flex: 1,
              minWidth: "120px",
              boxSizing: "border-box",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#dc2626";
              e.target.style.borderColor = "#dc2626";
              e.target.style.color = "white";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(220, 38, 38, 0.15)";
              e.target.style.borderColor = "rgba(220, 38, 38, 0.3)";
              e.target.style.color = "#dc2626";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "#dc2626";
            }}
          >
            {isLoading ? 'Rejecting...' : (
              <>
                <FiX style={{ marginRight: '4px' }} />
                Reject Revision
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Approve Content Modal ----------------
function ApproveContentModal({ isOpen, onClose, onConfirm, approval }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      setIsLoading(false);
      onClose();
    } catch (error) {
      console.error('Error approving content:', error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Approve Content</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#d1fae5", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <FiCheckCircle size={32} color="#059669" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to approve this content?</p>
          
          {approval && (
            <div className="content-preview" style={{
              background: "#f9fafb",
              padding: "16px",
              borderRadius: "8px",
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>{approval.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Creator:</strong> {approval.author}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Category:</strong> {approval.category}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Submitted Date:</strong> {formatDateModal(approval.submittedAt)}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-actions" style={{ display: "flex", gap: "12px" }}>
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isLoading}
            style={{ flex: 1, minWidth: "120px" }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm ${isLoading ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              backgroundColor: "rgba(16, 185, 129, 0.15)",
              borderColor: "rgba(16, 185, 129, 0.3)",
              color: "#10b981",
              minHeight: "40px",
              height: "40px",
              maxHeight: "40px",
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              overflow: "hidden",
              flex: 1,
              minWidth: "120px",
              boxSizing: "border-box",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#10b981";
              e.target.style.borderColor = "#10b981";
              e.target.style.color = "white";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(16, 185, 129, 0.15)";
              e.target.style.borderColor = "rgba(16, 185, 129, 0.3)";
              e.target.style.color = "#10b981";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "#10b981";
            }}
          >
            {isLoading ? 'Approving...' : (
              <>
                <FiCheckCircle style={{ marginRight: '4px' }} />
                Approve
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Reject Content Modal ----------------
function RejectContentModal({ isOpen, onClose, onConfirm, approval }) {
  const [isLoading, setIsLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleReasonChange = (e) => {
    // Remove special characters, only allow letters, numbers, and spaces
    const value = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '').substring(0, 50);
    setRejectionReason(value);
    setError("");
  };

  const handleConfirm = async () => {
    if (!rejectionReason.trim()) {
      setError("Rejection reason is required");
      return;
    }
    
    setIsLoading(true);
    try {
      await onConfirm(rejectionReason);
      setIsLoading(false);
      setRejectionReason("");
      setError("");
      onClose();
    } catch (error) {
      console.error('Error rejecting content:', error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close modal-close-top-right" onClick={onClose}>
          <FiX />
        </button>
        <div className="modal-header">
          <h3 className="modal-title-white">Reject Content</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-icon" style={{ 
            width: "64px", 
            height: "64px", 
            borderRadius: "50%", 
            background: "#fee2e2", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px"
          }}>
            <FiX size={32} color="#dc2626" />
          </div>
          
          <p className="modal-subtitle">Are you sure you want to reject this content?</p>
          
          {approval && (
            <div className="content-preview" style={{ 
              background: "#f9fafb", 
              padding: "16px", 
              borderRadius: "8px", 
              marginTop: "16px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>{approval.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Creator:</strong> {approval.author}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Category:</strong> {approval.category}
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  <strong>Submitted Date:</strong> {formatDateModal(approval.submittedAt)}
                </div>
              </div>
            </div>
          )}
          
          <div style={{ marginTop: "16px", marginBottom: "-25px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#374151" }}>
              Rejection Reason <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              value={rejectionReason}
              onChange={handleReasonChange}
              placeholder="Enter rejection reason"
              maxLength={50}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: error ? "1px solid #dc2626" : "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                transition: "border-color 0.2s",
                height: "110px"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
              onBlur={(e) => e.target.style.borderColor = error ? "#dc2626" : "#d1d5db"}
            />
            {error && (
              <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px", marginBottom: "0" }}>
                {error}
              </p>
            )}
          </div>
        </div>
        
        <div className="modal-actions" style={{ display: "flex", gap: "12px" }}>
          <button 
            type="button" 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
            disabled={isLoading}
            style={{ flex: 1, minWidth: "120px" }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className={`modal-btn modal-btn-confirm ${isLoading ? "loading" : ""}`}
            onClick={handleConfirm}
            disabled={isLoading}
            style={{
              backgroundColor: "rgba(220, 38, 38, 0.15)",
              borderColor: "rgba(220, 38, 38, 0.3)",
              color: "#dc2626",
              minHeight: "40px",
              height: "40px",
              maxHeight: "40px",
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              overflow: "hidden",
              flex: 1,
              minWidth: "120px",
              boxSizing: "border-box",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#dc2626";
              e.target.style.borderColor = "#dc2626";
              e.target.style.color = "white";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(220, 38, 38, 0.15)";
              e.target.style.borderColor = "rgba(220, 38, 38, 0.3)";
              e.target.style.color = "#dc2626";
              const icon = e.target.querySelector('svg');
              if (icon) icon.style.color = "#dc2626";
            }}
          >
            {isLoading ? 'Rejecting...' : (
              <>
                <FiX style={{ marginRight: '4px' }} />
                Reject
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting, activeTab }) {
  const [selectedFormat, setSelectedFormat] = useState(null);

  useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format for {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// Main Content Management Component
export default function AdminContent() {
  const { getTranslations } = useLanguage();
  const t = getTranslations(true);
  const navigate = useNavigate();
  const [allApprovals, setAllApprovals] = useState([]);
  const [allRevisions, setAllRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filterStatus, setFilterStatus] = useState("Pending");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApplyRevisionModal, setShowApplyRevisionModal] = useState(false);
  const [showRejectRevisionModal, setShowRejectRevisionModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [currentActionApproval, setCurrentActionApproval] = useState(null);
  const [currentActionRevision, setCurrentActionRevision] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreApprovals, setHasMoreApprovals] = useState(true);
  const [hasMoreRevisions, setHasMoreRevisions] = useState(true);
  const [lastApprovalDoc, setLastApprovalDoc] = useState(null);
  const [lastRevisionDoc, setLastRevisionDoc] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  // Action button tooltip handlers
  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  // Export handlers
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });

      switch (format) {
        case 'pdf':
          await exportToPDF(reportDate);
          break;
        case 'excel':
          await exportToExcel(reportDate);
          break;
        case 'csv':
          await exportToCSV(reportDate);
          break;
        default:
          throw new Error('Invalid export format');
      }
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPDF = async (reportDate) => {
    const pdf = createPDF({
      title: "Content Management Report"
    });

    // Prepare data for table
    const headers = ['Submission ID', 'Creator', 'Title', 'Category', 'Status'];
    const rows = filteredData.map(item => {
      return [
        item.id,
        item.author,
        item.title,
        item.category,
        item.status || 'Revision'
      ];
    });

    pdf.autoTable({
      head: [headers],
      body: rows
    });

    pdf.save(`content-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async (reportDate) => {
    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ['CONTENT MANAGEMENT REPORT'],
      ['Report Date', reportDate],
      ['Filter Status', filterStatus],
      [],
      ['CONTENT SUMMARY'],
      ['Total Items', filteredData.length],
      ['Pending', filteredData.filter(item => item.status === 'Pending').length],
      ['Approved', filteredData.filter(item => item.status === 'Approved').length],
      ['Rejected', filteredData.filter(item => item.status === 'Rejected').length],
      ['Archived', filteredData.filter(item => item.status === 'Archived').length],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Content details
    const contentData = [
      ['CONTENT DETAILS'],
      ['Submission ID', 'Creator', 'Title', 'Category', 'Status'],
      ...filteredData.map(item => [
        item.id,
        item.author,
        item.title,
        item.category,
        item.status || 'Revision'
      ])
    ];
    const contentSheet = XLSX.utils.aoa_to_sheet(contentData);
    XLSX.utils.book_append_sheet(workbook, contentSheet, 'Content');

    XLSX.writeFile(workbook, `content-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async (reportDate) => {
    let csvData = [
      ['CONTENT MANAGEMENT REPORT'],
      ['Report Date', reportDate],
      ['Filter Status', filterStatus],
      [],
      ['CONTENT SUMMARY'],
      ['Total Items', filteredData.length],
      ['Pending', filteredData.filter(item => item.status === 'Pending').length],
      ['Approved', filteredData.filter(item => item.status === 'Approved').length],
      ['Rejected', filteredData.filter(item => item.status === 'Rejected').length],
      ['Archived', filteredData.filter(item => item.status === 'Archived').length],
      [],
      ['CONTENT DETAILS'],
      ['Submission ID', 'Creator', 'Title', 'Category', 'Status'],
    ];

    csvData.push(...filteredData.map(item => [
      item.id,
      item.author,
      item.title,
      item.category,
      item.status || 'Revision'
    ]));

    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `content-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Filter approvals or revisions locally based on filterStatus, search, and category.
  // Memoized — only recomputes when data or filters actually change.
  const filteredData = useMemo(() => filterStatus === "Revisions"
    ? allRevisions.filter(rev => {
        const matchesSearch =
          search === "" ||
          rev.title?.toLowerCase().includes(search.toLowerCase()) ||
          rev.author?.toLowerCase().includes(search.toLowerCase()) ||
          rev.category?.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = categoryFilter === "" || rev.category === categoryFilter;
        return matchesSearch && matchesCategory;
      })
    : allApprovals.filter(acc => {
        const matchesStatus = acc.status === filterStatus;
        const matchesSearch =
          search === "" ||
          acc.title?.toLowerCase().includes(search.toLowerCase()) ||
          acc.author?.toLowerCase().includes(search.toLowerCase()) ||
          acc.category?.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = categoryFilter === "" || acc.category === categoryFilter;
        return matchesStatus && matchesSearch && matchesCategory;
      }),
  [allApprovals, allRevisions, filterStatus, search, categoryFilter]);

  // Memoize tab counts — avoids 4 redundant filter passes on every render
  const tabCounts = useMemo(() => ({
    pending:  allApprovals.filter(a => a.status === "Pending").length,
    approved: allApprovals.filter(a => a.status === "Approved").length,
    rejected: allApprovals.filter(a => a.status === "Rejected").length,
    archived: allApprovals.filter(a => a.status === "Archived").length,
    revisions: allRevisions.length,
  }), [allApprovals, allRevisions]);

  // Use filteredData for pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredData.slice(startIndex, endIndex);

  const showLoadMore = (filterStatus === "Revisions" ? hasMoreRevisions : hasMoreApprovals) &&
                       currentPage >= totalPages - 1;

  // Real-time listeners — replaces getDocs so Firestore can serve the
  // locally-cached snapshot instantly on re-visits (same behaviour as
  // the Appointments tab which uses onSnapshot).
  useEffect(() => {
    // Only show full-page spinner on first load (no data yet)
    if (allApprovals.length === 0 && allRevisions.length === 0) {
      setLoading(true);
    }

    let approvalsLoaded = false;
    let revisionsLoaded = false;
    const checkDone = () => {
      if (approvalsLoaded && revisionsLoaded) {
        setLoading(false);
        setIsRefreshing(false);
      }
    };

    const approvalsQuery = query(
      collection(db, 'approvals'),
      limit(100)
    );
    const revisionsQuery = query(
      collection(db, 'revisions'),
      limit(100)
    );

    const unsubApprovals = onSnapshot(approvalsQuery, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort newest-first client-side (avoids needing a server-side orderBy index)
      docs.sort((a, b) => {
        const at = a.submittedAt?.seconds ?? 0;
        const bt = b.submittedAt?.seconds ?? 0;
        return bt - at;
      });
      setAllApprovals(docs);
      approvalsLoaded = true;
      checkDone();
    }, (err) => {
      console.error('approvals listener error:', err);
      setLoading(false);
      setIsRefreshing(false);
    });

    const unsubRevisions = onSnapshot(revisionsQuery, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort newest-first client-side
      docs.sort((a, b) => {
        const at = a.createdAt?.seconds ?? 0;
        const bt = b.createdAt?.seconds ?? 0;
        return bt - at;
      });
      setAllRevisions(docs);
      revisionsLoaded = true;
      checkDone();
    }, (err) => {
      console.error('revisions listener error:', err);
      setLoading(false);
      setIsRefreshing(false);
    });

    return () => {
      unsubApprovals();
      unsubRevisions();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]); // refreshKey bump re-subscribes listeners; live updates handle mutations automatically

  // Reset to page 1 when filter, search, or category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, search, categoryFilter]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    } else if ((filterStatus === "Revisions" ? hasMoreRevisions : hasMoreApprovals) && !loadingMore) {
      // Load more data when reaching the end
      fetchApprovals(true);
    }
  };

  const fetchApprovals = async (loadMore = false) => {
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // Reset pagination state on fresh load
        setLastApprovalDoc(null);
        setLastRevisionDoc(null);
        setHasMoreApprovals(true);
        setHasMoreRevisions(true);
        setAllApprovals([]);
        setAllRevisions([]);
      }

      const [approvalsResult, revisionsResult] = await Promise.all([
        approvalService.getPaginatedApprovals(100, loadMore ? lastApprovalDoc : null),
        revisionService.getPaginatedRevisions(100, loadMore ? lastRevisionDoc : null)
      ]);

      if (loadMore) {
        setAllApprovals(prev => [...prev, ...approvalsResult.data]);
        setAllRevisions(prev => [...prev, ...revisionsResult.data]);
        setLastApprovalDoc(approvalsResult.lastDoc);
        setLastRevisionDoc(revisionsResult.lastDoc);
        setHasMoreApprovals(approvalsResult.hasMore);
        setHasMoreRevisions(revisionsResult.hasMore);
      } else {
        setAllApprovals(approvalsResult.data);
        setAllRevisions(revisionsResult.data);
        setLastApprovalDoc(approvalsResult.lastDoc);
        setLastRevisionDoc(revisionsResult.lastDoc);
        setHasMoreApprovals(approvalsResult.hasMore);
        setHasMoreRevisions(revisionsResult.hasMore);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleApplyRevision = (revision) => {
    setCurrentActionRevision(revision);
    setShowApplyRevisionModal(true);
  };

  const confirmApplyRevision = async () => {
    if (!currentActionRevision) return;
    try {
      // Update published collection
      const updateData = {
        title: currentActionRevision.title,
        subtitle: currentActionRevision.subtitle,
        bodyHtml: currentActionRevision.bodyHtml,
        tags: currentActionRevision.tags,
        category: currentActionRevision.category,
        socialTitle: currentActionRevision.socialTitle,
        socialDescription: currentActionRevision.socialDescription,
        socialImageUrl: currentActionRevision.socialImageUrl,
        minorEligible: currentActionRevision.minorEligible,
        updatedAt: serverTimestamp(),
      };

      if (currentActionRevision.contentId) {
        await publishedService.updatePublished(currentActionRevision.contentId, updateData);
      }

      // Find and update approval document if originalApprovalId exists
      if (currentActionRevision.originalApprovalId) {
        const approvalDoc = await approvalService.getApprovalById(currentActionRevision.originalApprovalId);
        if (approvalDoc) {
          await approvalService.updateApproval(approvalDoc.id, updateData);
        }
      }

      // Delete the revision document
      await revisionService.deleteRevision(currentActionRevision.id);

      // onSnapshot listener auto-updates the UI
      setShowApplyRevisionModal(false);
      setCurrentActionRevision(null);
    } catch (error) {
      console.error("Error applying revision:", error);
      alert("Failed to apply revision. Please try again.");
    }
  };

  const handleRejectRevision = (revision) => {
    setCurrentActionRevision(revision);
    setShowRejectRevisionModal(true);
  };

  const confirmRejectRevision = async () => {
    if (!currentActionRevision) return;
    try {
      // Delete the revision document
      await revisionService.deleteRevision(currentActionRevision.id);
      // onSnapshot listener auto-updates the UI
      setShowRejectRevisionModal(false);
      setCurrentActionRevision(null);
    } catch (error) {
      console.error("Error rejecting revision:", error);
      alert("Failed to reject revision. Please try again.");
    }
  };

  const handleApplyRevisionModalClose = () => {
    setShowApplyRevisionModal(false);
    setCurrentActionRevision(null);
  };

  const handleRejectRevisionModalClose = () => {
    setShowRejectRevisionModal(false);
    setCurrentActionRevision(null);
  };

  const handleApproveModalClose = () => {
    setShowApproveModal(false);
    setCurrentActionApproval(null);
  };

  const handleRejectModalClose = () => {
    setShowRejectionModal(false);
    setCurrentActionApproval(null);
  };

  const handleApprove = (approval) => {
    setCurrentActionApproval(approval);
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    if (!currentActionApproval) return;
    try {
      await approvalService.updateApprovalStatus(currentActionApproval.id, "Approved", {
        approvedAt: serverTimestamp(),
        approvedBy: "Admin"
      });
      // onSnapshot listener auto-updates the UI
      setShowApproveModal(false);
      setCurrentActionApproval(null);
    } catch (error) {
      console.error("Error approving content:", error);
      alert("Failed to approve content. Please try again.");
    }
  };

  const handleReject = (approval) => {
    setCurrentActionApproval(approval);
    setShowRejectionModal(true);
  };

  const confirmReject = async (rejectionReason) => {
    if (!currentActionApproval) return;
    try {
      await approvalService.updateApprovalStatus(currentActionApproval.id, "Rejected", {
        rejectedAt: serverTimestamp(),
        rejectedBy: "Admin",
        rejectReason: rejectionReason
      });
      // onSnapshot listener auto-updates the UI
      setShowRejectionModal(false);
      setCurrentActionApproval(null);
    } catch (error) {
      console.error("Error rejecting content:", error);
      alert("Failed to reject content. Please try again.");
    }
  };

  const handleArchive = (approval) => {
    setCurrentActionApproval(approval);
    setShowArchiveModal(true);
  };

  const confirmArchive = async () => {
    if (!currentActionApproval) return;
    
    try {
      // Remove from published collection
      if (currentActionApproval.contentId) {
        await publishedService.removeFromPublished(currentActionApproval.contentId);
      }
      
      // Update approval status to Archived
      await approvalService.updateApprovalStatus(currentActionApproval.id, "Archived", {
        archivedAt: serverTimestamp(),
        archivedBy: "Admin"
      });
      
      // onSnapshot listener auto-updates the UI
      setCurrentActionApproval(null);
    } catch (error) {
      console.error("Error archiving content:", error);
      alert("Failed to archive content. Please try again.");
    }
  };

  const handleRestore = (approval) => {
    setCurrentActionApproval(approval);
    setShowRestoreModal(true);
  };

  const confirmRestore = async () => {
    if (!currentActionApproval) return;
    
    try {
      // Add back to published collection
      await publishedService.addToPublished({
        title: currentActionApproval.title,
        subtitle: currentActionApproval.subtitle,
        bodyHtml: currentActionApproval.bodyHtml,
        socialTitle: currentActionApproval.socialTitle,
        socialDescription: currentActionApproval.socialDescription,
        socialImageUrl: currentActionApproval.socialImageUrl,
        category: currentActionApproval.category,
        tags: currentActionApproval.tags || [],
        author: currentActionApproval.author,
        authorEmail: currentActionApproval.authorEmail,
        authorRole: currentActionApproval.authorRole,
        authorId: currentActionApproval.authorId,
        approvedAt: currentActionApproval.approvedAt,
        approvedBy: currentActionApproval.approvedBy,
        contentId: currentActionApproval.contentId
      });
      
      // Update approval status back to Approved
      await approvalService.updateApprovalStatus(currentActionApproval.id, "Approved", {
        restoredAt: serverTimestamp(),
        restoredBy: "Admin"
      });
      
      // onSnapshot listener auto-updates the UI
      setCurrentActionApproval(null);
    } catch (error) {
      console.error("Error restoring content:", error);
      alert("Failed to restore content. Please try again.");
    }
  };

  const handleArchiveModalClose = () => {
    setShowArchiveModal(false);
    setCurrentActionApproval(null);
  };

  const handleRestoreModalClose = () => {
    setShowRestoreModal(false);
    setCurrentActionApproval(null);
  };

  const handleDelete = (approval) => {
    setCurrentActionApproval(approval);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!currentActionApproval) return;
    
    try {
      await approvalService.deleteApproval(currentActionApproval.id);
      // onSnapshot listener auto-updates the UI
      setCurrentActionApproval(null);
    } catch (error) {
      console.error("Error deleting content:", error);
      alert("Failed to delete content. Please try again.");
    }
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setCurrentActionApproval(null);
  };

  const handleRowClick = (approval) => {
    // Use contentId for navigation, fallback to document ID if contentId is not available
    const contentIdForNavigation = approval.contentId || approval.id;
    navigate(`/admin/content-review/${contentIdForNavigation}`);
  };

  return (
    <div className="account-management-container">
      {/* ── Custom Tooltip for Action Buttons ── */}
      {tooltip.visible && (
        <div
          className={`admin-action-tooltip ${tooltip.type}`}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}
      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Administrator - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Administrator
            </h2>
          </div>
        </div>

        {/* Title and Subtitle - Right Aligned */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            {t.contentManagement}
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            Review and manage submitted content for approval
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>

      {/* Search and Refresh Button Row */}
      <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
            placeholder={`${t.search} ${t.title}, ${t.creator}, ${t.category}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="admin-aura-select"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
        >
          <option value="">{t.allCategories}</option>
          {CONTENT_CATEGORIES.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        <button
          onClick={() => {
            setIsRefreshing(true);
            setRefreshKey(k => k + 1);
          }}
          disabled={isRefreshing}
          className="admin-primary-gradient-btn"
          style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
        >
          <FiRefreshCw className={isRefreshing ? "spinning" : ""} /> {isRefreshing ? "Refreshing..." : "Refresh"}
        </button>
        <button
          className="admin-primary-gradient-btn"
          onClick={() => setShowExportModal(true)}
        >
          <FiDownload /> Export Report
        </button>
      </div>

      {/* Tabs Row with Pagination */}
      <div className="content-filter-tabs-wrapper">
        <div className="content-filter-tabs">
          <button
            className={`content-filter-tab ${filterStatus === "Pending" ? "active" : ""}`}
            onClick={() => setFilterStatus("Pending")}
          >
            {t.pending}
            <span className="tab-count">
              {tabCounts.pending}
            </span>
          </button>
          <button
            className={`content-filter-tab ${filterStatus === "Approved" ? "active" : ""}`}
            onClick={() => setFilterStatus("Approved")}
          >
            {t.published}
            <span className="tab-count">
              {tabCounts.approved}
            </span>
          </button>
          <button
            className={`content-filter-tab ${filterStatus === "Revisions" ? "active" : ""}`}
            onClick={() => setFilterStatus("Revisions")}
          >
            {t.revisions}
            <span className="tab-count">
              {allRevisions.length}
            </span>
          </button>
          <button
            className={`content-filter-tab ${filterStatus === "Rejected" ? "active" : ""}`}
            onClick={() => setFilterStatus("Rejected")}
          >
            {t.rejected}
            <span className="tab-count">
              {tabCounts.rejected}
            </span>
          </button>
          <button
            className={`content-filter-tab ${filterStatus === "Archived" ? "active" : ""}`}
            onClick={() => setFilterStatus("Archived")}
          >
            {t.archived}
            <span className="tab-count">
              {tabCounts.archived}
            </span>
          </button>
        </div>
        
        {/* Right Side: Pagination + Export PDF */}
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <div style={{ fontSize: "14px", color: "#6b7280" }}>
            {filteredData.length > 0 ? (
              `${startIndex + 1}-${Math.min(endIndex, filteredData.length)} of ${filteredData.length}`
            ) : (
              '0 of 0'
            )}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button 
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{ 
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white', 
                border: 'none', 
                padding: '8px 12px', 
                borderRadius: '4px', 
                cursor: 'pointer',
                width: '32px'
              }}
            >
              ←
            </button>
            
            <button 
              onClick={handleNextPage}
              disabled={currentPage === totalPages || totalPages === 0 || loadingMore}
              style={{ 
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white', 
                border: 'none', 
                padding: '8px 12px', 
                borderRadius: '4px', 
                cursor: 'pointer',
                width: '32px',
                opacity: loadingMore ? 0.5 : 1
              }}
            >
              {loadingMore ? '...' : '→'}
            </button>
          </div>
        </div>
      </div>

      {/* Content Table */}
      <div className="admin-aura-table-wrapper" style={{ marginTop: 10 }}>
        <table className="admin-aura-table">
          <thead>
            <tr>
              <th className="admin-aura-col-image">Image</th>
              <th className="admin-aura-col-creator">{t.creator}</th>
              <th className="admin-aura-col-title">{t.title}</th>
              <th className="admin-aura-col-category">{t.category}</th>
              {filterStatus === "Revisions" ? (
                <th className="admin-aura-col-date">{t.revisionDate}</th>
              ) : (
                <th className="admin-aura-col-date">{t.submittedDate}</th>
              )}
              {(filterStatus === "Approved" || filterStatus === "Rejected" || filterStatus === "Archived") && <th className="admin-aura-col-date">{t.decisionDate}</th>}
              {filterStatus === "Rejected" && <th className="admin-aura-col-reason">{t.reasonForRejection}</th>}
              <th className="admin-aura-col-actions">{t.actions}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[...Array(10)].map((_, i) => (
                  <tr key={i} className="admin-aura-skeleton-row">
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '40px', height: '40px', borderRadius: '4px' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 4) * 10}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '70%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '65%' }} /></td>
                    {(filterStatus === "Approved" || filterStatus === "Rejected" || filterStatus === "Archived") && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '55%' }} /></td>}
                    {filterStatus === "Rejected" && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${50 + (i % 3) * 15}%` }} /></td>}
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                  </tr>
                ))}
              </>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={
                  5 + 
                  ((filterStatus === "Approved" || filterStatus === "Rejected" || filterStatus === "Archived") ? 1 : 0) +
                  (filterStatus === "Rejected" ? 1 : 0)
                } style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                  {t.noContentFound}
                </td>
              </tr>
            ) : (
              currentItems.map((item) => (
                filterStatus === "Revisions" ? (
                  <tr 
                    key={item.id} 
                    onClick={() => handleRowClick(item)}
                    className="admin-aura-clickable-row"
                  >
                    <td className="admin-aura-col-image">
                      {item.socialImageUrl ? (
                        <img 
                          src={item.socialImageUrl} 
                          alt={item.title}
                          style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                        />
                      ) : (
                        <div style={{ width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", color: "#6b7280" }}>
                          N/A
                        </div>
                      )}
                    </td>
                    <td className="admin-aura-col-creator">{item.author}</td>
                    <td className="admin-aura-col-title">{item.title}</td>
                    <td className="admin-aura-col-category">{item.category}</td>
                    <td className="admin-aura-col-date">{formatDate(item.createdAt || item.updatedAt)}</td>
                    <td className="admin-aura-actions admin-aura-col-actions">
                      <button 
                        type="button"
                        className="ma-action-btn approve"
                        onClick={(e) => { e.stopPropagation(); handleApplyRevision(item); }}
                        aria-label="Apply"
                        onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; e.target.style.color = '#059669'; handleActionMouseEnter(e, "Apply Revision", 'approve'); }}
                        onMouseMove={handleActionMouseMove}
                        onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; e.target.style.color = '#059669'; handleActionMouseLeave(); }}
                        style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981', border: '1px solid', borderRadius: '12px' }}
                      >
                        <FiCheckCircle size={16} />
                      </button>
                      <button 
                        type="button"
                        className="ma-action-btn cancel"
                        onClick={(e) => { e.stopPropagation(); handleRejectRevision(item); }}
                        aria-label="Reject"
                        onMouseEnter={(e) => { e.target.style.background = '#fecaca'; e.target.style.color = '#dc2626'; handleActionMouseEnter(e, "Reject Revision", 'delete'); }}
                        onMouseMove={handleActionMouseMove}
                        onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; e.target.style.color = '#dc2626'; handleActionMouseLeave(); }}
                        style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444', border: '1px solid', borderRadius: '12px' }}
                      >
                        <FiX size={16} />
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr 
                    key={item.id} 
                    onClick={() => handleRowClick(item)}
                    className="admin-aura-clickable-row"
                  >
                    <td className="admin-aura-col-image">
                      {item.socialImageUrl ? (
                        <img 
                          src={item.socialImageUrl} 
                          alt={item.title}
                          style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                        />
                      ) : (
                        <div style={{ width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", color: "#6b7280" }}>
                          N/A
                        </div>
                      )}
                    </td>
                    <td className="admin-aura-col-creator">{item.author}</td>
                    <td className="admin-aura-col-title">{item.title}</td>
                    <td className="admin-aura-col-category">{item.category}</td>
                    <td className="admin-aura-col-date">{formatDate(item.submittedAt)}</td>

                    {(filterStatus === "Approved" || filterStatus === "Rejected" || filterStatus === "Archived") && (
                      <td className="admin-aura-col-date">
                        {item.status === "Approved" ? formatDate(item.restoredAt || item.approvedAt) :
                         item.status === "Archived" ? formatDate(item.archivedAt) :
                         item.status === "Rejected" ? formatDate(item.rejectedAt) : "N/A"}
                      </td>
                    )}

                    {filterStatus === "Rejected" && (
                      <td className="admin-aura-col-reason">
                        {item.rejectReason || 'N/A'}
                      </td>
                    )}

                    <td className="admin-aura-actions admin-aura-col-actions">
                      {item.status === "Pending" ? (
                        <>
                          <button 
                            type="button"
                            className="ma-action-btn approve"
                            onClick={(e) => { e.stopPropagation(); handleApprove(item); }}
                            aria-label="Approve"
                            onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; e.target.style.color = '#059669'; handleActionMouseEnter(e, "Approve Content", 'approve'); }}
                            onMouseMove={handleActionMouseMove}
                            onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; e.target.style.color = '#059669'; handleActionMouseLeave(); }}
                            style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981', border: '1px solid', borderRadius: '12px' }}
                          >
                            <FiCheckCircle size={16} />
                          </button>
                          <button 
                            type="button"
                            className="ma-action-btn cancel"
                            onClick={(e) => { e.stopPropagation(); handleReject(item); }}
                            aria-label="Reject"
                            onMouseEnter={(e) => { e.target.style.background = '#fecaca'; e.target.style.color = '#dc2626'; handleActionMouseEnter(e, "Reject Content", 'delete'); }}
                            onMouseMove={handleActionMouseMove}
                            onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; e.target.style.color = '#dc2626'; handleActionMouseLeave(); }}
                            style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444', border: '1px solid', borderRadius: '12px' }}
                          >
                            <FiX size={16} />
                          </button>
                          <button 
                            type="button"
                            className="ma-action-btn approve"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/revise-content/${item.id}`); }}
                            aria-label="Revise"
                            onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; e.target.style.color = '#1d4ed8'; handleActionMouseEnter(e, "Revise Content", 'view'); }}
                            onMouseMove={handleActionMouseMove}
                            onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; e.target.style.color = '#1d4ed8'; handleActionMouseLeave(); }}
                            style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6', border: '1px solid', borderRadius: '12px' }}
                          >
                            <FiEdit2 size={16} />
                          </button>
                        </>
                      ) : item.status === "Approved" ? (
                        <>
                          <button 
                            type="button"
                            className="ma-action-btn approve"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/revise-content/${item.id}`); }}
                            aria-label="Revise"
                            onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; e.target.style.color = '#1d4ed8'; handleActionMouseEnter(e, "Revise Content", 'view'); }}
                            onMouseMove={handleActionMouseMove}
                            onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; e.target.style.color = '#1d4ed8'; handleActionMouseLeave(); }}
                            style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6', border: '1px solid', borderRadius: '12px' }}
                          >
                            <FiEdit2 size={16} />
                          </button>
                          <button 
                            type="button"
                            className="ma-action-btn cancel"
                            onClick={(e) => { e.stopPropagation(); handleArchive(item); }}
                            aria-label="Archive"
                            onMouseEnter={(e) => { e.target.style.background = '#fecaca'; e.target.style.color = '#dc2626'; handleActionMouseEnter(e, "Archive Content", 'delete'); }}
                            onMouseMove={handleActionMouseMove}
                            onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; e.target.style.color = '#dc2626'; handleActionMouseLeave(); }}
                            style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444', border: '1px solid', borderRadius: '12px' }}
                          >
                            <MdOutlineArchive size={16} />
                          </button>
                        </>
                      ) : item.status === "Archived" ? (
                        <>
                          <button 
                            type="button"
                            className="ma-action-btn approve"
                            onClick={(e) => { e.stopPropagation(); handleRestore(item); }}
                            aria-label="Restore"
                            onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; e.target.style.color = '#059669'; handleActionMouseEnter(e, "Restore Content", 'approve'); }}
                            onMouseMove={handleActionMouseMove}
                            onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; e.target.style.color = '#059669'; handleActionMouseLeave(); }}
                            style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981', border: '1px solid', borderRadius: '12px' }}
                          >
                            <FiRefreshCw size={16} />
                          </button>
                          <button 
                            type="button"
                            className="ma-action-btn cancel"
                            onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                            aria-label="Delete"
                            onMouseEnter={(e) => { e.target.style.background = '#fecaca'; e.target.style.color = '#dc2626'; handleActionMouseEnter(e, "Delete Content", 'delete'); }}
                            onMouseMove={handleActionMouseMove}
                            onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; e.target.style.color = '#dc2626'; handleActionMouseLeave(); }}
                            style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444', border: '1px solid', borderRadius: '12px' }}
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </>
                      ) : item.status === "Rejected" ? (
                        <button 
                          type="button"
                          className="ma-action-btn cancel"
                          onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                          aria-label="Delete"
                          onMouseEnter={(e) => { e.target.style.background = '#fecaca'; e.target.style.color = '#dc2626'; handleActionMouseEnter(e, "Delete Content", 'delete'); }}
                          onMouseMove={handleActionMouseMove}
                          onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; e.target.style.color = '#dc2626'; handleActionMouseLeave(); }}
                          style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444', border: '1px solid', borderRadius: '12px' }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                )
              ))
            )}
          </tbody>
        </table>
      </div>
      
      <ApproveContentModal
        isOpen={showApproveModal}
        onClose={handleApproveModalClose}
        onConfirm={confirmApprove}
        approval={currentActionApproval}
      />
      
      <RejectContentModal
        isOpen={showRejectionModal}
        onClose={handleRejectModalClose}
        onConfirm={confirmReject}
        approval={currentActionApproval}
      />
      
      <ArchiveModal
        isOpen={showArchiveModal}
        onClose={handleArchiveModalClose}
        onConfirm={confirmArchive}
        approval={currentActionApproval}
      />
      
      <RestoreModal
        isOpen={showRestoreModal}
        onClose={handleRestoreModalClose}
        onConfirm={confirmRestore}
        approval={currentActionApproval}
      />
      
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteModalClose}
        onConfirm={confirmDelete}
        approval={currentActionApproval}
      />
      
      <ApplyRevisionModal
        isOpen={showApplyRevisionModal}
        onClose={handleApplyRevisionModalClose}
        onConfirm={confirmApplyRevision}
        revision={currentActionRevision}
      />
      
      <RejectRevisionModal
        isOpen={showRejectRevisionModal}
        onClose={handleRejectRevisionModalClose}
        onConfirm={confirmRejectRevision}
        revision={currentActionRevision}
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
        activeTab={filterStatus}
      />
    </div>
  );
}